import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { DataTable, DataTableSkeleton, dataTableFeatures } from "@/components/ui/data-table";
import { naarZoekparameters, type Filterdescriptor } from "@/components/ui/data-table-filter";
import { Pager } from "@/components/ui/pager";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { useBeheerKorpsenPaged, type Korps } from "@/features/wedstrijden/useWedstrijden";

const helper = createColumnHelper<typeof dataTableFeatures, Korps>();

/** The `sortBy` value the API understands for each sortable column. */
const SORT_BY: Record<string, string> = { city: "city" };

/**
 * Plaats is a column and not a parenthesis after the naam: half the korpsen are named after
 * their plaats and half are not, and it is what tells two "Jeugdbrandweer Noord" apart.
 */
const kolommen = (t: TFunction<"ploegen">) =>
  helper.columns([
    helper.accessor("name", {
      header: t("korpsen.korps"),
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    helper.accessor("city", { header: t("korpsen.plaats") }),
  ]);

export function KorpsenTable({
  korpsId,
  onKies,
  periodeId,
  pagina,
  onPaginaChange,
}: {
  korpsId: string | undefined;
  onKies: (korpsId: string) => void;
  /** The periode `PloegenDetail` has open - what the "already entered" filter checks against. */
  periodeId: string | undefined;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation("ploegen");
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = sorting[0];

  // Server-driven, so the descriptor carries no `past` and the facet shows no count (#192):
  // this list is one page of an answer the server already narrowed.
  const descriptors = useMemo<Filterdescriptor<Korps>[]>(
    () => [
      {
        id: "reedsIngeschreven",
        label: t("korpsen.filterReedsIngeschreven"),
        kind: "keuze",
        opties: [
          { waarde: "verbergen", label: t("korpsen.filterReedsIngeschrevenVerbergen") },
          { waarde: "alleen", label: t("korpsen.filterReedsIngeschrevenAlleen") },
        ],
      },
    ],
    [t],
  );
  const { stand, onStand } = useFilterStand(descriptors, "/ploegen");
  const reedsIngeschreven = naarZoekparameters(stand).reedsIngeschreven as string | undefined;

  const { data: page, isPending } = useBeheerKorpsenPaged(
    pagina,
    FULL_PAGE_SIZE,
    search,
    sort && SORT_BY[sort.id],
    sort?.desc ?? false,
    periodeId,
    reedsIngeschreven,
  );

  const columns = kolommen(t);

  if (isPending || !page) {
    return <DataTableSkeleton label={t("korpsen.label")} columns={columns} rows={6} search />;
  }

  return (
    <div className="space-y-4">
      <DataTable
        label={t("korpsen.label")}
        data={page.items}
        columns={columns}
        getRowId={(korps) => korps.id}
        selectedId={korpsId}
        onSelect={onKies}
        searchPlaceholder={t("korpsen.zoek")}
        empty={t("korpsen.leeg")}
        exportHref={exportHref("/api/beheer/korpsen/export", {
          query: {
            search,
            sortBy: sort && SORT_BY[sort.id],
            sortDescending: sort?.desc ?? false,
            inschrijfperiodeId: periodeId,
            reedsIngeschreven,
          },
        })}
        serverDriven={{
          sorting,
          onSortingChange: setSorting,
          globalFilter: search,
          // A search narrows the set the server counts, so page 2 of the old set would
          // otherwise land past the end of the new, shorter one.
          onGlobalFilterChange: (value) => {
            setSearch(value);
            onPaginaChange(1);
          },
        }}
        filters={{
          descriptors,
          stand,
          // Same reason as the search box above: a filter narrows what the server counts, so
          // the old page can land past the end of the new, shorter one.
          onStand: (nieuw) => {
            onStand(nieuw);
            onPaginaChange(1);
          },
          toonTelling: false,
        }}
      />
      <Pager
        page={page.page}
        pageSize={page.pageSize}
        totalCount={page.totalCount}
        onPageChange={onPaginaChange}
      />
    </div>
  );
}
